// @flow

import * as R from "ramda";
import React, { type Node } from "react";
import styled, { type StyledComponent, keyframes } from "styled-components";
import type { Theme } from "views/resources/theme";

const fxDot = keyframes`
  50% {
    transform: scale(1);
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
`;

type ContainerProps = {|
	button: boolean,
	margin: number,
	children: Node,
|};

const Container: StyledComponent<ContainerProps, Theme, *> = styled.div`
	padding: ${({ button, size }) =>
		button ? (size === "full" ? "8px 0px" : "4px 0px") : "0px 0px"};
	height: ${({ full }) => (full ? "100%" : "auto")};
	text-align: center;
	margin: ${({ margin }) => `${margin}px 0px`};
	width: ${({ size }) => (size === "full" ? "auto" : "120%")};
	background: inherit;
	margin-left: -10px;

	${({ full }) =>
		full &&
		`
		background: ${({ full }) => (full ? "#fffe" : "#fff")};
		position: absolute;
		top: 0;
		width: 100%;
		margin-left: 0;
		left: 0;
	`}

	@media screen and (min-width: 768px) {
		& {
			margin-left: 0;
			line-height: inherit;
			width: 100%;
		}
	}
`;

const Dot = styled.div`
	width: 12px;
	height: 12px;
	border: 2px solid ${({ dark, color }) => (dark ? "#fff" : color)};
	background: ${({ dark, color }) => (dark ? "#fff" : color)};
	border-radius: 50%;
	float: left;
	margin: 0 5px;
	transform: scale(0);
	animation: fxDot 1100ms ease infinite 0ms;

	&:nth-child(1n) {
		animation: ${fxDot} 1100ms ease-in infinite
			${({ idx }) => 200 * (idx - 1)}ms;
	}
`;

const Center = styled.div`
	width: ${({ length }) => length * 28} px;
	position: relative;
	top: 50%;
	transform: translateY(-50%);
	margin-top: 14px;
	display: inline-block;
`;

const maxLength = (len, max = 3) => (len > max ? max : len);

interface Props {
	length?: number;
	dark?: boolean;
	button: boolean;
	margin?: number;
	color?: string;
	size?: string;
	full: boolean;
}

const Spinner = ({
	color = "#08798c",
	length = 3,
	dark = false,
	button = false,
	margin = 0,
	full = false,
	size = "medium",
}: Props) => (
	<Container button={button} full={full} margin={margin} size={size}>
		<Center length={length}>
			{R.times((dot) => (
				<Dot dark={dark} color={color} idx={dot} key={dot} />
			))(maxLength(length))}
		</Center>
	</Container>
);

export default Spinner;
